import { isEmptyGameParams, useGameParamsContext } from "./gameParams";
import React from "react";
import { z } from "zod";

import { captureManualException } from "../tracing/sentry";

import {
    getRewardStatus,
    SpellFactoryRewardResponse,
} from "../clients/inbox-rewards";
import { getAvatarId } from "../utils/jwt";

const LOCAL_STORAGE_KEY = "inbox-rewards";

const InboxRewardsState = z.record(
    z.number().or(z.string()),
    z.object({
        rewards: SpellFactoryRewardResponse,
    }),
);

type InboxRewardsState = z.infer<typeof InboxRewardsState>;

const defaultState = {};

const InboxRewardsContext = React.createContext<{
    state: InboxRewardsState;
    actions: {
        fetchUnclaimedVouchers: (voucherIds: string[]) => void;
        claimVoucher: (voucherId: string) => void;
        getClaimedVouchers: () => string[];
    };
}>({
    state: defaultState,
    actions: {
        fetchUnclaimedVouchers: (_voucherIds: string[]) => null,
        claimVoucher: (_voucherId: string) => null,
        getClaimedVouchers: () => [],
    },
});

type SessionAction = {
    type: "UPDATE_REWARD_STATUS";
    reward: SpellFactoryRewardResponse;
    avatarId: string | number;
};

const reducer = (
    state: InboxRewardsState,
    action: SessionAction,
): InboxRewardsState => {
    const getNewState = (state: InboxRewardsState, action: SessionAction) => {
        switch (action.type) {
            case "UPDATE_REWARD_STATUS": {
                return {
                    ...state,

                    [action.avatarId]: {
                        ...state[action.avatarId],
                        rewards: {
                            ...state[action.avatarId]?.rewards,
                            ...action.reward,
                        },
                    },
                };
            }
            default: {
                throw new Error(`Unhandled action type: ${action.type}`);
            }
        }
    };

    const newState = getNewState(state, action);

    saveToLocalStorage(newState);

    return newState;
};

const InboxRewardsContextProvider = ({
    children,
    initialState,
}: {
    children: JSX.Element | JSX.Element[];
    initialState: InboxRewardsState;
}): JSX.Element | null => {
    const { gameParams } = useGameParamsContext();
    const jwt = !isEmptyGameParams(gameParams) ? gameParams.jwt : undefined;

    const avatarId = getAvatarId(jwt ?? "") ?? "";

    const [state, dispatch] = React.useReducer(reducer, initialState);

    const fetchUnclaimedVouchers = async (vouchers: string[]) => {
        if (!jwt) {
            console.error("No JWT available");
            return;
        }

        const claimedVouchers = Object.entries(state[avatarId]?.rewards ?? {})
            .filter(([_voucherId, reward]) => {
                return reward.status === "VOUCHER_REDEEMED";
            })
            .map(([voucherId, _reward]) => voucherId);

        const vouchersToPoll = vouchers.filter(
            (v) => !claimedVouchers.includes(v),
        );

        if (vouchersToPoll.length === 0) {
            return;
        }

        const reward = await getRewardStatus(vouchersToPoll, jwt);

        if (reward) {
            dispatch({
                type: "UPDATE_REWARD_STATUS",
                reward,
                avatarId,
            });
        }
    };

    const claimVoucher = (voucherId: string) => {
        const url = `action=voucher&code=${voucherId}`;
        window.sendCommand?.(url);
    };

    const getClaimedVouchers = () => {
        const claimed = Object.entries(state[avatarId]?.rewards ?? {})
            .filter(([_voucherId, reward]) => {
                return reward.status === "VOUCHER_REDEEMED";
            })
            .map(([voucherId, _reward]) => voucherId);

        return claimed;
    };

    return (
        <InboxRewardsContext.Provider
            value={{
                state,
                actions: {
                    fetchUnclaimedVouchers,
                    claimVoucher,
                    getClaimedVouchers,
                },
            }}
        >
            {children}
        </InboxRewardsContext.Provider>
    );
};

export const useInboxRewardsContext = () =>
    React.useContext(InboxRewardsContext);

export const withInboxRewardsContextProvider =
    (Component: typeof React.Component) => (props: Record<string, unknown>) => {
        return (
            <InboxRewardsContextProvider initialState={readFromLocalStorage()}>
                <Component {...props} />
            </InboxRewardsContextProvider>
        );
    };

const saveToLocalStorage = (state: InboxRewardsState) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
};

const readFromLocalStorage = (): InboxRewardsState => {
    try {
        const state = JSON.parse(
            localStorage.getItem(LOCAL_STORAGE_KEY) || "{}",
        );

        return InboxRewardsState.parse(state);
    } catch (e) {
        captureManualException(e as Error);

        localStorage.removeItem(LOCAL_STORAGE_KEY);

        return defaultState;
    }
};
